// What is wrong with the tour, computed from the config alone.
//
// Scope is deliberately narrow: the faults an operator can create in the
// editor and cannot see there. Today that is one — a navigation hotspot with
// no destination — and it is worth its own module because of how it failed.
//
// The room picker stubs `targetRoomId: ''` when a hotspot becomes a
// navigation, and renders that as "— selecione —". Coercing it to the first
// room instead would be worse (a link that silently goes somewhere nobody
// chose), so the blank is correct — but nothing marked it as UNFINISHED, and
// a dropdown sitting on a plausible-looking placeholder reads as a resting
// state rather than a to-do. Measured on quadrimoveis-3q 2026-09-01: an
// operator placed 19 navigation hotspots across 65 minutes and set the
// destination on none of them. The first thing that noticed was
// `validate-rooms` inside a Vercel build, two weeks later, and it refused the
// whole publish — one blank arrow blocks every room.
//
// THIS IS THE THIRD IMPLEMENTATION OF THIS RULE, and that is a known cost.
// scripts/validate-rooms.mjs owns it for the build; the portal's
// validateTourConfig owns it at queue time (separate repo, cannot import);
// this owns it in the editor, where the mistake is actually made. Each catches
// it at a different moment and only this one can catch it before it is saved.

import type { RoomsConfig } from './types';

export interface NavProblem {
  roomId: string;
  /** Room name as shown to the operator, not the id. */
  roomName: string;
  hotspotId: string;
  /** The hotspot's own label — what the operator sees on the dot. */
  label: string;
  /** '' when nothing was chosen, otherwise a room id that does not exist. */
  target: string;
}

/** Navigation hotspots that point nowhere, in config order. */
export function findNavProblems(config: RoomsConfig | null | undefined): NavProblem[] {
  if (!config?.rooms) return [];
  const ids = new Set(config.rooms.map((r) => r.id));
  const problems: NavProblem[] = [];
  for (const room of config.rooms) {
    for (const h of room.hotspots ?? []) {
      if (h.type !== 'navigation') continue;
      if (typeof h.targetRoomId === 'string' && ids.has(h.targetRoomId)) continue;
      problems.push({
        roomId: room.id,
        roomName: nameOf(room.name, room.id),
        hotspotId: h.id,
        label: nameOf(h.label, h.id),
        target: typeof h.targetRoomId === 'string' ? h.targetRoomId : '',
      });
    }
  }
  return problems;
}

/** True when this specific destination value is unusable. */
export function isBadNavTarget(config: RoomsConfig | null | undefined, target: unknown): boolean {
  if (typeof target !== 'string' || target === '') return true;
  return !(config?.rooms ?? []).some((r) => r.id === target);
}

// Localised fields are resolved in place before any consumer runs
// (src/app/localizeConfig.ts), so a string is the normal case here; the object
// branch only covers a config read before that pass.
function nameOf(value: unknown, fallback: string): string {
  if (typeof value === 'string' && value) return value;
  if (value && typeof value === 'object') {
    const pt = (value as Record<string, unknown>)['pt-BR'];
    if (typeof pt === 'string' && pt) return pt;
  }
  return fallback;
}
